import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import maskPhoneNumber from '@lib/utils/maskPhoneNumber';
import { SearchParams } from '@components/CustomerSearch/components/CustomerSearchBox/CustomerSearchBox';
import {
  CustomerBySearchType,
  CustomersBySearchResponse,
} from '@components/CustomerSearch/data/types';
import { ITask } from '@modules/TasksModule/types';
import useVoiceTask from '@modules/TasksModule/hooks/useVoiceTask';

interface SystemCustomerSearchProps {
  task: ITask;
  tenantId: string | undefined;
  setSearchParams: (params: SearchParams) => void;
  onSelectCustomer: (customer?: Partial<CustomerBySearchType>) => void;
  search: (
    params: SearchParams,
  ) => Promise<CustomersBySearchResponse | undefined>;
}

export const useSystemCustomerSearch = ({
  task,
  tenantId,
  onSelectCustomer,
  setSearchParams,
  search,
}: SystemCustomerSearchProps) => {
  const [isSystemCustomerNotFound, setIsSystemCustomerNotFound] =
    useState(false);
  const systemSearchParams = useMemo(() => {
    return {
      phone: task?.from ? maskPhoneNumber(task.from) : undefined,
    };
  }, [task]);
  const { isInternal } = useVoiceTask(task);
  const initSearchCompleted = useRef(false);

  const systemCustomerSearchInitialLoad = useCallback(async () => {
    if (isInternal) {
      // for internal calls, we do not search for customers
      setIsSystemCustomerNotFound(false);
      initSearchCompleted.current = true;
      return;
    }
    if (!initSearchCompleted.current && tenantId && systemSearchParams.phone) {
      const customerResults = await search(systemSearchParams);

      if (customerResults) {
        if (customerResults.customers.length === 0) {
          setIsSystemCustomerNotFound(true);
        } else {
          setIsSystemCustomerNotFound(false);
          setSearchParams(systemSearchParams);
          if (customerResults.customers.length === 1) {
            onSelectCustomer(customerResults.customers[0]);
          }
        }
      }
      initSearchCompleted.current = true;
    } else if (!initSearchCompleted.current) {
      setSearchParams(systemSearchParams);
      initSearchCompleted.current = true;
    }
  }, [
    isInternal,
    tenantId,
    systemSearchParams,
    search,
    setSearchParams,
    onSelectCustomer,
  ]);

  useEffect(() => {
    systemCustomerSearchInitialLoad();
  }, [systemCustomerSearchInitialLoad]);

  return {
    isSystemCustomerNotFound,
  };
};
